import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom'
import {
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Badge,
    Card,
    CardBody,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    NavItem,
    Row,
    TabContent,
    TabPane,
    UncontrolledAccordion,
    UncontrolledDropdown,
} from 'reactstrap'
import contacts from '../../api/contacts/hooks'
import { ContactType, HistoryState, PermissionsProps, RouteParams } from '../../api/globalTypes'
import { MODAL_TYPES } from '../../common/constants'
import { companyGeneralViewData } from '../../common/views/companyViewData'
import ActivitiesViewAccordion from '../../Components/ActivitiesViewAccordion'
import AppPermissionsSwitcher from '../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../Components/AppPermissionsSwitcher/constants/actions'
import { EntityParamsProvider } from '../../Components/AppPermissionsSwitcher/EntityParamsContext'
import AppTopSwitcher from '../../Components/AppTopSwitcher/AppTopSwitcher'
import BackButton from '../../Components/BackButton/BackButton'
import FormTabNavigation from '../../Components/Common/FormTabNavigation'
import Loader from '../../Components/Common/Loader'
import NavigationActions from '../../Components/Common/NavigationActions/NavigationActions'
import PageTitle from '../../Components/Common/PageTitle'
import ContactProfileViewTab from '../../Components/ContactProfileViewTab/ContactProfileViewTab'
import { DocumentTable } from '../../Components/Documents'
import InDepthAnalysisList from '../../Components/InDepthAnalysis/InDepthAnalysisList/InDepthAnalysisList'
import { setDeleteCallback } from '../../Components/Modals/DeleteModal/utils'
import NotesViewAccordion from '../../Components/NotesViewAccordion'
import QueryErrorBoundary from '../../Components/QueryErrorBoundary/QueryErrorBoundary'
import { useColumnOrder } from '../../Components/TanstackTable'
import { showModal } from '../../store/modal'
import { selectSearchCriteriaCompanies } from '../../store/search'
import { formatDateTime } from '../../utils/general'
import { contactViewTabs } from '../Contacts/constants'
import { ViewData } from '../Contacts/ContactView/ContactView.types'

const CompanyView = () => {
    const { id } = useParams<RouteParams>()

    return (
        <div className="page-content">
            <Container fluid>
                <QueryErrorBoundary resetKeys={[id]}>
                    <CompanyViewContent id={id} />
                </QueryErrorBoundary>
            </Container>
        </div>
    )
}

const CompanyViewContent = ({ id }: { id: string }) => {
    const history = useHistory<HistoryState>()
    const location = useLocation()
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const searchCriteria = useSelector(selectSearchCriteriaCompanies)
    const order = useColumnOrder('company')

    const params = {
        criteria: { ...searchCriteria, contactType: ContactType.Company },
        order,
    }

    const { data: companyData, isPending } = contacts.GetCompany.useQuery(id, params, { throwOnError: true })
    const company = !isPending ? companyData?.item : null

    const { mutate: deleteCompany } = contacts.DeleteContact.useMutation()

    const tabs = contactViewTabs()
    const viewData: ViewData = companyGeneralViewData(company)

    const [activeTab, setActiveTab] = useState<string | number | undefined>(history.location?.state?.activeTab ?? tabs[0].name)

    const handleTabChange = (tab: string) => {
        if (activeTab !== tab) {
            setActiveTab(tab)
        }
    }

    const handleDelete = () => {
        const callbackId = 'delete-company'
        const payload = {
            modalType: MODAL_TYPES.DELETE_MODAL,
            modalProps: { callbackId },
        }

        setDeleteCallback(callbackId, () => deleteCompany({ id: +id, contactType: ContactType.Company }))
        dispatch(showModal(payload))
    }

    if (isPending) {
        return <Loader />
    }

    document.title = `${company?.contactCompany?.name ?? t('app.common.companies')} | ${t('app.title')}`

    return (
        <EntityParamsProvider value={params}>
            <AppPermissionsSwitcher permission="ModulePermissions" module={location} action={action.view}>
                {({ hasModulePermission }: PermissionsProps) => hasModulePermission === false && <Redirect to="/companies" />}
            </AppPermissionsSwitcher>
            <div className="page-title-box ps-0 d-sm-flex align-items-start mt-3">
                <BackButton className="me-3" />
                <div>
                    <PageTitle
                        className="px-2"
                        title={`${company?.contactCompany?.name ?? ''}`}
                        titleBadgeComponent={<Badge color="favorite">{company?.contactPriority?.name}</Badge>}
                    />

                    {company?.contactStatus && (
                        <Row>
                            <Col sm="12">
                                <div className="px-2">
                                    <span className="fw-medium">{t('app.common.contact.profileStatus')} </span>
                                    {company?.contactStatus?.name}
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col sm="12">
                            <div className="px-2">
                                <span className="fw-medium">{t('app.common.contact.establishmentDate')}:</span>{' '}
                                {formatDateTime(company?.initialContactDate)}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <Row className="d-flex justify-content-between align-items-center mt-5 mb-3">
                <Col sm={12} md={8}>
                    <FormTabNavigation tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange} customClass="detail-view-tabs">
                        <NavItem>
                            <UncontrolledDropdown>
                                <DropdownToggle title={t('table.showMore')} href="#" className="real-estate-form-nav-link nav-link dropdown" tag="a">
                                    <i className="ri-more-fill align-middle"></i>
                                </DropdownToggle>
                                <DropdownMenu className="dropdown-menu-end">
                                    <AppPermissionsSwitcher permission="ModulePermissions" module={location} action={action.edit}>
                                        {({ hasModulePermission }: PermissionsProps) =>
                                            hasModulePermission && (
                                                <>
                                                    <DropdownItem onClick={() => history.push(`/companies/${id}/edit`)}>
                                                        <i className="mdi mdi-pencil-outline align-bottom me-2 text-muted"></i>{' '}
                                                        {t('view.company.action.edit')}
                                                    </DropdownItem>
                                                    <DropdownItem onClick={() => handleDelete()}>
                                                        <i className="mdi mdi-trash-can-outline align-bottom me-2 text-muted"></i>{' '}
                                                        {t('view.company.action.delete')}
                                                    </DropdownItem>
                                                </>
                                            )
                                        }
                                    </AppPermissionsSwitcher>
                                    <DropdownItem onClick={() => history.push(`/companies/${id}/edit`, { activeTab: 'demand' })}>
                                        <i className="mdi mdi-plus align-bottom me-2 text-muted"></i> {t('view.contact.action.addProfile')}
                                    </DropdownItem>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </NavItem>
                    </FormTabNavigation>
                </Col>
                <Col sm={12} md={4}>
                    <NavigationActions links={companyData?.links} />
                </Col>
            </Row>

            <TabContent activeTab={activeTab} className="text-muted">
                {activeTab === 'general' && (
                    <TabPane tabId="general">
                        <Row>
                            <Col sm={12} xxl={3}>
                                <Card>
                                    <CardBody>
                                        {viewData?.general &&
                                            viewData.general
                                                .filter((viewItem) => viewItem.value)
                                                .map((viewItem, index) => {
                                                    if (viewItem.value) {
                                                        return (
                                                            <Row
                                                                className={`align-items-start ${
                                                                    viewData.general!.filter((x) => x.value).length - 1 !== index ? 'mb-4' : ''
                                                                }`}
                                                                key={viewItem?.id}
                                                            >
                                                                <Col sm={6}>
                                                                    <span className="flex-grow-1 fw-medium">{viewItem?.label}:</span>
                                                                </Col>
                                                                <Col sm={6}>
                                                                    <span className="flex-grow-1 fw-light">{viewItem?.value}</span>
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }
                                                    return null
                                                })}
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col sm={12} xxl={9}>
                                <UncontrolledAccordion defaultOpen={['1']} stayOpen toggle={() => null}>
                                    <AccordionItem className="mb-4">
                                        <AccordionHeader targetId="1">
                                            <h5>{t('view.company.queryData')}</h5>
                                        </AccordionHeader>
                                        <AccordionBody accordionId="1">
                                            <Row className="mt-4">
                                                <Col sm={12} className="fw-light text-secondary">
                                                    {company?.notes}
                                                </Col>
                                            </Row>
                                        </AccordionBody>
                                    </AccordionItem>
                                </UncontrolledAccordion>

                                <NotesViewAccordion entityId={id} noteEntity={'contact'} />

                                <AppTopSwitcher
                                    feature="ActivitiesAccordion"
                                    permission="ModulePermissions"
                                    permissionName={'module.activity.view'}
                                    module={location}
                                    action={action.view}
                                >
                                    {(props: { featureActivitiesAccordion: unknown; hasModulePermission: unknown }) => {
                                        const { featureActivitiesAccordion, hasModulePermission: permissionActivitiesAccordion } = props
                                        const allowAccordion = featureActivitiesAccordion && permissionActivitiesAccordion

                                        return (
                                            allowAccordion && (
                                                <ActivitiesViewAccordion contactType={ContactType.Company} parentId={id} location={location} />
                                            )
                                        )
                                    }}
                                </AppTopSwitcher>
                            </Col>
                        </Row>
                    </TabPane>
                )}
                {(activeTab === 'demand' || activeTab === 'offer') && (
                    <TabPane tabId={activeTab}>
                        <ContactProfileViewTab
                            key={activeTab}
                            contactId={id}
                            contactType={ContactType.Company}
                            entity={company}
                            activeTab={activeTab}
                        />
                    </TabPane>
                )}
                {activeTab === 'documents' && (
                    <TabPane tabId="documents">
                        <Card>
                            <CardBody>
                                <DocumentTable
                                    parentId={id}
                                    documentModule="contact"
                                    contactType={ContactType.Company}
                                    onAddClick={() => {
                                        history.push(`/companies/${id}/edit`, { activeTab: 'documents' })
                                    }}
                                />
                            </CardBody>
                        </Card>
                    </TabPane>
                )}
                {activeTab === 'inDepthAnalysis' && (
                    <TabPane tabId="inDepthAnalysis">
                        <InDepthAnalysisList formId={2} contactType="companies" />
                    </TabPane>
                )}
            </TabContent>
        </EntityParamsProvider>
    )
}

export default CompanyView
